import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { getStoredTheme, setTheme } from "@/lib/theme";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { MessageCircle, Megaphone, Calendar, BarChart3, Users, LogOut, Sparkles, Link2, Shield, CreditCard, LayoutDashboard, Crown, MessageSquare, Menu, Crosshair, EyeOff } from "lucide-react";
import NotificationBell from "@/components/NotificationBell";
import InvitesDialog from "@/components/InvitesDialog";

const TAB = {
  chat: { to: "/app/chat", label: "Chat", icon: MessageCircle, testid: "nav-chat" },
  announcements: { to: "/app/announcements", label: "Announcements", icon: Megaphone, testid: "nav-announcements" },
  events: { to: "/app/events", label: "Events", icon: Calendar, testid: "nav-events" },
  polls: { to: "/app/polls", label: "Polls", icon: BarChart3, testid: "nav-polls" },
  members: { to: "/app/members", label: "Members", icon: Users, testid: "nav-members" },
  messages: { to: "/app/messages", label: "Messages", icon: MessageSquare, testid: "nav-messages" },
  membership: { to: "/app/membership", label: "Membership", icon: CreditCard, testid: "nav-membership" },
  ar15: { to: "/app/ar15", label: "AR-15", icon: Crosshair, testid: "nav-ar15" },
  admin: { to: "/app/admin", label: "Dashboard", icon: LayoutDashboard, testid: "nav-admin" },
};

function buildTabs(user) {
  const role = user?.role;
  const isOwner = role === "owner";
  const isAdmin = role === "admin" || isOwner;
  const category = user?.category;
  const canViewChat = isAdmin || (!!user?.is_active_member && category !== "AR-15" && category !== "friend");

  if (!isAdmin && !category && !user?.is_active_member) {
    return []; // locked — no access until an admin grants access
  }
  if (!isAdmin && category === "AR-15") {
    return [TAB.events, TAB.ar15, TAB.membership];
  }
  if (!isAdmin && category === "friend") {
    return [TAB.events, TAB.announcements, TAB.membership];
  }
  const tabs = [];
  if (canViewChat) tabs.push(TAB.chat);
  tabs.push(TAB.announcements, TAB.events, TAB.polls, TAB.members, TAB.messages, TAB.membership);
  if (isAdmin) tabs.push(TAB.ar15);
  if (isOwner) tabs.push(TAB.admin);
  return tabs;
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [theme, setThemeState] = useState(getStoredTheme());

  const chooseTheme = (t) => { setTheme(t); setThemeState(t); };

  const isAdmin = user?.role === "admin" || user?.role === "owner";
  const isOwner = user?.role === "owner";
  const tabs = buildTabs(user);

  // Access guard: keep users out of sections their membership doesn't allow
  useEffect(() => {
    if (!user) return;
    const path = location.pathname;
    if (path === "/app" || path === "/app/") return;
    if (tabs.length === 0) return; // locked users stay on the locked screen
    const allowed = tabs.some((t) => path === t.to || path.startsWith(t.to + "/"));
    if (!allowed) {
      navigate(tabs[0]?.to || "/app/events", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, user]);

  const locked = !!user && tabs.length === 0;

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen">
      <div
        className="fixed inset-0 -z-10 pointer-events-none"
        style={{
          backgroundImage: "url(/umhb-bg.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <header className="glass-nav sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-4">
          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="menu-trigger"
                className="group flex items-center gap-2 focus:outline-none"
                aria-label="Open menu"
              >
                <img src="/cfaa-mark.png" alt="CFAA" className="h-11 w-auto rounded-lg object-contain shadow-sm group-hover:opacity-90 transition-opacity" />
                <Menu className="w-5 h-5 text-slate-500 group-hover:text-purple-700 transition-colors" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0 bg-white">
              <div className="p-6 border-b border-slate-100">
                <img src="/cfaa-mark.png" alt="Crusader Football Alumni Association" className="h-16 w-auto rounded-lg object-contain" />
              </div>
              <nav className="flex flex-col gap-1 p-4" data-testid="menu-nav">
                {tabs.map(({ to, label, icon: Icon, testid }) => (
                  <NavLink
                    key={to}
                    to={to}
                    onClick={() => setMenuOpen(false)}
                    data-testid={`${testid}-menu`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-4 py-3 rounded-xl font-semibold transition-colors ${
                        isActive ? "bg-purple-700 text-white shadow" : "text-slate-700 hover:bg-slate-100"
                      }`
                    }
                  >
                    <Icon className="w-5 h-5" />
                    {label}
                  </NavLink>
                ))}
              </nav>
            </SheetContent>
          </Sheet>

          <div className="ml-auto flex items-center gap-3">
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="user-menu-btn" className="focus:outline-none">
                <Avatar className="w-10 h-10 ring-2 ring-purple-200 cursor-pointer">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-amber-400 text-slate-900 font-bold">
                    {user?.name?.[0]?.toUpperCase() || "U"}
                  </AvatarFallback>
                </Avatar>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-2xl w-56">
                <DropdownMenuLabel className="font-display">
                  <div className="font-bold text-slate-900 flex items-center gap-1">
                    {user?.name}
                    {isOwner ? <Crown className="w-3.5 h-3.5 text-amber-500" /> : user?.role === "admin" && <Shield className="w-3.5 h-3.5 text-purple-600" />}
                  </div>
                  <div className="text-xs text-slate-500 font-normal">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {isAdmin && (
                  <InvitesDialog
                    trigger={
                      <DropdownMenuItem
                        data-testid="invites-menu-item"
                        onSelect={(e) => e.preventDefault()}
                        className="cursor-pointer rounded-xl"
                      >
                        <Link2 className="w-4 h-4 mr-2" /> Invite links
                      </DropdownMenuItem>
                    }
                  />
                )}
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs font-semibold text-slate-500 py-1">Appearance</DropdownMenuLabel>
                <DropdownMenuItem
                  onClick={() => chooseTheme("light")}
                  data-testid="theme-light-btn"
                  className={`cursor-pointer rounded-xl ${theme === "light" ? "bg-amber-50 font-bold" : ""}`}
                >
                  <span className="mr-2 text-base" aria-hidden>☀️</span> Light mode
                  {theme === "light" && <span className="ml-auto text-amber-500">✓</span>}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => chooseTheme("dark")}
                  data-testid="theme-dark-btn"
                  className={`cursor-pointer rounded-xl ${theme === "dark" ? "bg-purple-100 font-bold" : ""}`}
                >
                  <span className="mr-2 text-base" aria-hidden>🌙</span> Dark mode
                  {theme === "dark" && <span className="ml-auto text-purple-600">✓</span>}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={doLogout} data-testid="logout-btn" className="cursor-pointer rounded-xl">
                  <LogOut className="w-4 h-4 mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {locked ? (
          <div data-testid="locked-screen" className="max-w-lg mx-auto text-center bg-white rounded-3xl shadow-sm border border-slate-100 p-10 mt-10">
            <div className="w-14 h-14 rounded-2xl bg-slate-900 flex items-center justify-center mx-auto">
              <Shield className="w-7 h-7 text-amber-400" />
            </div>
            <h1 className="mt-6 text-2xl font-black font-display text-slate-900">Access pending</h1>
            <p className="mt-3 text-slate-500">
              Your account is approved but hasn't been given a membership yet. An owner or admin will grant you access soon — check back shortly.
            </p>
          </div>
        ) : (
          <>
            {user?.read_only && (
              <div data-testid="readonly-banner" className="mb-6 flex flex-col sm:flex-row sm:items-center gap-3 bg-amber-50 border border-amber-200 rounded-2xl px-5 py-4">
                <div className="flex items-start gap-3 flex-1">
                  <EyeOff className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold text-amber-800 text-sm">You're in read-only mode</p>
                    <p className="text-amber-700 text-xs mt-0.5">You can view everything and RSVP to events, but you can't comment, post or vote until your dues are paid.</p>
                  </div>
                </div>
                <NavLink
                  to="/app/membership"
                  data-testid="readonly-pay-dues-btn"
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-amber-600 hover:bg-amber-700 text-white text-sm font-bold whitespace-nowrap"
                >
                  Pay dues
                </NavLink>
              </div>
            )}
            <Outlet />
          </>
        )}
      </main>
    </div>
  );
}
